import { Box, HStack, Text, Image, Popover, Portal, VStack } from "@chakra-ui/react";

import BannerImage from "../../assets/images/banner.jpg";
import "./layout.css";
import CmsIcon from "../../assets/icons/cms-icon.png";
import NovaLogo from "../../assets/icons/nova-icon.svg";
import AccessabilityIcon from "../../assets/icons/accessability-icon.svg";
import NotificationIcon from "../../assets/icons/notification-icon.svg";
import LanguageSelector from "../ui/LanguageSelector";
import Notifications from "../ui/Notifications";
import { Avatar } from "../ui/Avatar";
import { getUserInitials } from "../../helpers/color";
import DemoBadge from "../ui/DemoBadge";
import TeamsService from "../../services/TeamsService";

interface HeaderProps {
  userName: string;
}

export default function Header({ userName }: HeaderProps) {
  const userRole = sessionStorage.getItem("user_role");

  const handleSignOut = () => {
    TeamsService.getInstance().signOut();
  };

  return (
    <Box
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
      position="sticky"
      top="0"
      zIndex={10}
    >
      <HStack justify="space-between" align="stretch" px={6} gap={4} h="60px">
        {/* Logo */}
        <HStack gap={2} align="center" flexShrink={0}>
          <Image src={NovaLogo} alt="Nova Logo" w="32px" h="32px" />
          <Text className="header-nova-text">NOVA</Text>
          <DemoBadge />
        </HStack>
        
        {/* Welcome Banner - Takes full width */}
        <HStack
          bg={`url(${BannerImage})`}
          backgroundSize="cover"
          px={4}
          flex="1"
          justify="space-between"
          align="center"
        >
          <Box>
            <Text
              fontSize="sm"
              fontWeight="600"
              color="white"
            >
              Good Morning {userName.split(" ")[0] || "Guest"}!
            </Text>
            <Text fontSize="xs" color="white">
              Welcome to Orange County Collaborative Courts' Case Management Hub!
            </Text>
          </Box>
          <Image src={CmsIcon} alt="CMS Icon" w="40px" h="40px" flexShrink={0} />
        </HStack>
        
        {/* User Controls */}
        <HStack gap={4} flexShrink={0}>
          <LanguageSelector />
          <Image
            src={AccessabilityIcon}
            alt="Accessability Icon"
            w="24px"
            h="24px"
          />
          <Notifications
            trigger={
              <Box
                as="button"
                cursor="pointer"
                _hover={{ opacity: 0.7 }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  src={NotificationIcon}
                  alt="Notification Icon"
                  w="24px"
                  h="24px"
                />
              </Box>
            }
          />

          {/* User Avatar with sign-out menu */}
          <Popover.Root positioning={{ placement: "bottom-end" }}>
            <Popover.Trigger asChild>
              <Box as="button" cursor="pointer" _hover={{ opacity: 0.8 }}>
                <Avatar name={userName} initials={getUserInitials(userName) || undefined} />
              </Box>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content w="220px" p={0} borderRadius="12px">
                  <VStack align="stretch" gap={0}>
                    <Box p={4} borderBottom="1px solid #D1DADF">
                      <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                        {userName || "User"}
                      </Text>
                      {userRole && (
                        <Text fontSize="xs" color="gray.500">
                          {userRole}
                        </Text>
                      )}
                    </Box>
                    <Box
                      as="button"
                      onClick={handleSignOut}
                      textAlign="left"
                      px={4}
                      py={3}
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      borderRadius="0 0 12px 12px"
                    >
                      <Text fontSize="sm" color="red.500" fontWeight="500">
                        Sign out
                      </Text>
                    </Box>
                  </VStack>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        </HStack>
      </HStack>
    </Box>
  );
}

export const WelcomeBanner = ({ userName }: { userName: string }) => {
  return (
    <HStack
      flex="1"
      bg={`url(${BannerImage})`}
      backgroundSize="cover"
      position="relative"
      overflow="hidden"
      justify="space-between"
      p={4}
    >
      <Box>
        <Text
          className="header-large-text"
          fontWeight="600"
          position="relative"
        >
          Good Morning {userName.split(" ")[0] || "Guest"}!
        </Text>
        <Text className="header-subtitle-text" position="relative">
          Welcome to Orange County Collaborative Courts' Case Management Hub!
        </Text>
      </Box>
      <Image src={CmsIcon} alt="CMS Icon" w="64px" h="64px" />
    </HStack>
  );
};
